import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";

interface RankedChunk {
  name: string;
  initialScore: number;
  rerankScore: number;
  initialRank: number;
  finalRank: number;
}

const CHUNKS: RankedChunk[] = [
  { name: "src/lib/rag/map-reduce-response.ts", initialScore: 0.96, rerankScore: 9.4, initialRank: 1, finalRank: 1 },
  { name: "src/lib/rag/graph-augmented-retrieval.ts", initialScore: 0.91, rerankScore: 9.1, initialRank: 3, finalRank: 2 },
  { name: "src/lib/rag/entry-point-tracer.ts", initialScore: 0.93, rerankScore: 8.7, initialRank: 2, finalRank: 3 },
  { name: "src/lib/rag/reranker.ts", initialScore: 0.90, rerankScore: 8.3, initialRank: 4, finalRank: 4 },
  { name: "src/lib/chat/completion.ts", initialScore: 0.87, rerankScore: 7.6, initialRank: 5, finalRank: 5 },
];

// Chunks that get filtered out
const FILTERED = [
  { name: "src/lib/rag/embeddings.ts", score: 3.2 },
  { name: "src/app/layout.tsx", score: 2.5 },
];

export const Scene6Reranking: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  const exitOpacity = interpolate(frame, [210, 240], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Phase: initial list (0-80), LLM scoring (80-140), reordered list (140+)
  const showReranked = frame > 140;

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.06} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 50,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
        }}
      >
        <div style={{ fontSize: 42, fontFamily: FONTS.heading, fontWeight: 700, color: COLORS.textPrimary }}>
          Step 7:{" "}
          <span style={{ color: COLORS.accentPink }}>Rerank, Diversify & Token-Gate</span>
        </div>
        <div style={{ fontSize: 18, fontFamily: FONTS.body, color: COLORS.textSecondary, marginTop: 8 }}>
          Cross-encoder reranks by relevance → file-diversity quota → pre-LLM token gate
        </div>
      </div>

      {/* Left: Before reranking */}
      <div
        style={{
          position: "absolute",
          left: 100,
          top: 190,
          width: 560,
        }}
      >
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 13,
            color: COLORS.textMuted,
            textTransform: "uppercase",
            letterSpacing: 1.5,
            marginBottom: 14,
            opacity: interpolate(
              spring({ frame: frame - 15, fps, config: SPRING.snappy }),
              [0, 1],
              [0, 1],
            ),
          }}
        >
          Before: 15 chunks (search order)
        </div>

        {/* Show initial ordering */}
        {CHUNKS.map((chunk, i) => {
          const rowAppear = spring({ frame: frame - 20 - i * 8, fps, config: SPRING.snappy });

          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 16px",
                marginBottom: 8,
                borderRadius: 10,
                background: COLORS.bgCard,
                border: `1px solid ${COLORS.borderLight}`,
                opacity: interpolate(rowAppear, [0, 1], [0, showReranked ? 0.4 : 1]),
                transform: `translateX(${interpolate(rowAppear, [0, 1], [-20, 0])}px)`,
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 14,
                  fontWeight: 700,
                  color: COLORS.textMuted,
                  minWidth: 24,
                }}
              >
                #{chunk.initialRank}
              </span>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  color: COLORS.textSecondary,
                  flex: 1,
                }}
              >
                {chunk.name}
              </span>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  color: COLORS.accentTeal,
                  fontWeight: 600,
                }}
              >
                {chunk.initialScore.toFixed(2)}
              </span>
            </div>
          );
        })}

        {/* Filtered chunks (greyed out) */}
        {FILTERED.map((chunk, i) => {
          const fAppear = spring({ frame: frame - 65 - i * 8, fps, config: SPRING.snappy });
          return (
            <div
              key={`f-${i}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 16px",
                marginBottom: 6,
                borderRadius: 8,
                background: `${COLORS.bgCard}60`,
                opacity: interpolate(fAppear, [0, 1], [0, 0.3]),
                fontFamily: FONTS.mono,
                fontSize: 12,
                color: COLORS.textMuted,
              }}
            >
              <span>...</span>
              <span style={{ flex: 1 }}>{chunk.name}</span>
              <span style={{ textDecoration: "line-through" }}>{chunk.score.toFixed(1)}</span>
            </div>
          );
        })}
      </div>

      {/* Center: LLM scoring animation */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: 440,
          transform: "translate(-50%, -50%)",
        }}
      >
        {(() => {
          const brainAppear = spring({ frame: frame - 80, fps, config: SPRING.bouncy });
          const isScoring = frame > 80 && frame < 140;

          return (
            <div
              style={{
                opacity: interpolate(brainAppear, [0, 1], [0, 1]),
                transform: `scale(${interpolate(brainAppear, [0, 1], [0.3, 1])})`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${COLORS.accentPink}30, ${COLORS.accentPurple}15)`,
                  border: `2px solid ${COLORS.accentPink}50`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isScoring ? `0 0 30px ${COLORS.accentPink}30` : "none",
                }}
              >
                <span style={{ fontSize: 40 }}>🧠</span>
              </div>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 12,
                  color: isScoring ? COLORS.accentPink : COLORS.textMuted,
                }}
              >
                {isScoring ? "Scoring chunks..." : "Cross-encoder"}
              </span>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 11,
                  color: COLORS.textMuted,
                }}
              >
                5 chunks/batch
              </span>
            </div>
          );
        })()}
      </div>

      {/* Right: After reranking */}
      <div
        style={{
          position: "absolute",
          right: 100,
          top: 190,
          width: 560,
        }}
      >
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 13,
            color: COLORS.textMuted,
            textTransform: "uppercase",
            letterSpacing: 1.5,
            marginBottom: 14,
            opacity: interpolate(
              spring({ frame: frame - 140, fps, config: SPRING.snappy }),
              [0, 1],
              [0, 1],
            ),
          }}
        >
          After: Top 5 (reranked by relevance)
        </div>

        {CHUNKS.map((chunk, i) => {
          const rowAppear = spring({ frame: frame - 145 - i * 10, fps, config: SPRING.snappy });

          // Color based on score
          const scoreColor =
            chunk.rerankScore >= 8.5
              ? COLORS.accentGreen
              : chunk.rerankScore >= 7
                ? COLORS.accentYellow
                : COLORS.accentOrange;

          // Did rank change?
          const moved = chunk.finalRank !== chunk.initialRank;

          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 18px",
                marginBottom: 10,
                borderRadius: 10,
                background: `${scoreColor}08`,
                border: `1.5px solid ${scoreColor}30`,
                opacity: interpolate(rowAppear, [0, 1], [0, 1]),
                transform: `translateX(${interpolate(rowAppear, [0, 1], [20, 0])}px)`,
              }}
            >
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 16,
                  fontWeight: 700,
                  color: scoreColor,
                  minWidth: 24,
                }}
              >
                #{chunk.finalRank}
              </span>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 13,
                  color: COLORS.textPrimary,
                  flex: 1,
                }}
              >
                {chunk.name}
              </span>
              {moved && (
                <span
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 11,
                    color: chunk.finalRank < chunk.initialRank ? COLORS.accentGreen : COLORS.accentRed,
                    padding: "2px 6px",
                    borderRadius: 4,
                    background:
                      chunk.finalRank < chunk.initialRank ? `${COLORS.accentGreen}15` : `${COLORS.accentRed}15`,
                  }}
                >
                  {chunk.finalRank < chunk.initialRank ? `↑${chunk.initialRank - chunk.finalRank}` : `↓${chunk.finalRank - chunk.initialRank}`}
                </span>
              )}
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 15,
                  fontWeight: 700,
                  color: scoreColor,
                  minWidth: 40,
                  textAlign: "right",
                }}
              >
                {chunk.rerankScore.toFixed(1)}
              </span>
            </div>
          );
        })}
      </div>

      {/* Bottom stats */}
      <div
        style={{
          position: "absolute",
          bottom: 70,
          left: "50%",
          transform: "translate(-50%, 0)",
          display: "flex",
          gap: 80,
        }}
      >
        {[
          { label: "Cross-encoder", value: "reranked", color: COLORS.accentPink, delay: 190 },
          { label: "File-Diversity Quota", value: "no single file", color: COLORS.accentPurple, delay: 200 },
          { label: "Pre-LLM Token Gate", value: "context capped", color: COLORS.accentTeal, delay: 210 },
        ].map((stat, i) => {
          const sAppear = spring({ frame: frame - stat.delay, fps, config: SPRING.snappy });
          return (
            <div key={i} style={{ textAlign: "center", opacity: interpolate(sAppear, [0, 1], [0, 1]) }}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 26, fontWeight: 700, color: stat.color }}>
                {stat.value}
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textMuted, marginTop: 4 }}>
                {stat.label}
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
